import { useCallback, useEffect } from 'react'
import { baseApolloClient } from 'src/services/apollo/client'
import { useSignInWithGoogleToken } from 'src/services/orpc/auth'
import { config } from 'src/utils/config'
import { useLocation } from 'src/utils/router/route'
import { getMayTokenBeValid } from 'src/utils/token'
import { track } from 'src/utils/tracker'

const DISMISSED_KEY = 'google_one_tap_dismissed'
const GSI_SCRIPT_ID = 'google-gsi-script'

/**
 * Browsing session timeout: if the user hasn't interacted in 30 minutes,
 * we consider it a new session and reset the dismissed state so the
 * user can be re-prompted. Using localStorage (instead of sessionStorage)
 * so the state is shared across tabs.
 */
const SESSION_TIMEOUT_MS = 30 * 60 * 1000

function isDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    if (!raw) return false
    const data = JSON.parse(raw)
    if (Date.now() - (data.ts || 0) > SESSION_TIMEOUT_MS) return false
    return data.dismissed === true
  } catch {
    return false
  }
}

function setDismissed() {
  try {
    localStorage.setItem(
      DISMISSED_KEY,
      JSON.stringify({ dismissed: true, ts: Date.now() }),
    )
  } catch {}
}

let gsiLoadPromise: Promise<void> | null = null

function loadGsiScript(): Promise<void> {
  if (gsiLoadPromise) return gsiLoadPromise

  gsiLoadPromise = new Promise((resolve, reject) => {
    // If the script tag already exists AND google API is ready, resolve immediately
    if (document.getElementById(GSI_SCRIPT_ID) && window.google?.accounts?.id) {
      resolve()
      return
    }
    // If the script tag exists but google isn't ready yet (e.g. still loading
    // after a React StrictMode remount), wait for it with a polling fallback
    if (document.getElementById(GSI_SCRIPT_ID)) {
      const check = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(check)
          resolve()
        }
      }, 50)
      // Give up after 10s
      setTimeout(() => {
        clearInterval(check)
        gsiLoadPromise = null
        reject(new Error('Google GSI script loaded but API not available'))
      }, 10_000)
      return
    }
    const script = document.createElement('script')
    script.id = GSI_SCRIPT_ID
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      gsiLoadPromise = null
      reject(new Error('Failed to load Google GSI script'))
    }
    document.head.appendChild(script)
  })

  return gsiLoadPromise
}

export function GoogleOneTap() {
  const { pathname } = useLocation()
  const { mutateAsync: signInWithGoogleToken } = useSignInWithGoogleToken()

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        track('google_one_tap_accept')
        const { isNew } = await signInWithGoogleToken({
          body: { token: response.credential },
        })
        // Token is automatically extracted from the `token` response header
        // by the orpcClient response interceptor and stored in localStorage.
        // UserPropertiesTracker and UserInfoChangeHandler (in Root.tsx)
        // will automatically handle trackerSetUserId, websocket
        // resubscription, and push token refresh.
        await baseApolloClient.resetStore()
        track(isNew ? 'register_success' : 'login_success', {
          type: 'google_one_tap',
        })
      } catch (err) {
        console.error('[GoogleOneTap] Sign-in failed:', err)
      }
    },
    [signInWithGoogleToken],
  )

  useEffect(() => {
    const clientId = config.google.oneTapClientId
    if (!clientId) return
    if (getMayTokenBeValid()) return
    if (isDismissed()) return
    let cancelled = false

    loadGsiScript()
      .then(() => {
        if (cancelled) return

        window.google?.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          cancel_on_tap_outside: true,
        })

        window.google?.accounts.id.prompt(notification => {
          if (notification.isDismissedMoment()) {
            track('google_one_tap_dismiss')
            setDismissed()
          }
          if (notification.isSkippedMoment()) {
            setDismissed()
          }
        })
      })
      .catch(err => {
        console.error('[GoogleOneTap] Failed to load:', err)
      })

    return () => {
      cancelled = true
      window.google?.accounts.id.cancel()
    }
  }, [pathname, handleCredentialResponse])

  return null
}
